import { supabase } from '@/lib/supabase';
import {
  ConsentBucket,
  ConsentRow,
  ConsentState,
  CURRENT_CONSENT_VERSION,
  EMPTY_CONSENT,
} from '@/types/consent';

// Current consent = latest row per bucket. Append-only history lives in DB.
export async function fetchConsent(userId: string): Promise<ConsentState> {
  const { data, error } = await supabase
    .from('user_consent')
    .select('bucket, granted, version, ts')
    .eq('user_id', userId)
    .order('ts', { ascending: false });

  if (error || !data) return { ...EMPTY_CONSENT, loadedAt: Date.now() };

  const seen = new Set<ConsentBucket>();
  const state: ConsentState = { ...EMPTY_CONSENT, loadedAt: Date.now() };

  for (const row of data as Pick<ConsentRow, 'bucket' | 'granted' | 'version' | 'ts'>[]) {
    if (seen.has(row.bucket)) continue;
    seen.add(row.bucket);
    state[row.bucket] = row.granted;
    if (!state.version) state.version = row.version;
  }

  // "Decided" = any row exists, regardless of grant outcome. Drives whether
  // the post-verify welcome screen has already been resolved.
  state.decidedA = seen.has('A');
  state.decidedB = seen.has('B');


  return state;
}

export async function recordConsent(
  userId: string,
  bucket: ConsentBucket,
  granted: boolean,
): Promise<void> {
  const { error } = await supabase.from('user_consent').insert({
    user_id: userId,
    bucket,
    granted,
    version: CURRENT_CONSENT_VERSION,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null,
  });
  if (error) {
    // Wrap so React error boundaries / Next.js overlay print a real message,
    // not "[object Object]" from a serialized PostgrestError.
    const msg = error.message || error.details || error.hint || 'unknown';
    const code = error.code ? ` [${error.code}]` : '';
    throw new Error(`Could not record consent: ${msg}${code}`);
  }
}

// Idempotent. RPC increments active_days_count once per UTC day.
export async function bumpActiveDay(): Promise<void> {
  await supabase.rpc('bump_active_day');
}
